/**
 * BOGO US Builder - Main JavaScript
 * US-specific flow:
 *   Visual Step 1: Choose primary bike (2 options)
 *   Hidden step:   Customize bike (color/variant + upgrades)
 *   Visual Step 2: Choose free bike
 *   Visual Step 3: Checkout / Confirmation (reuses bogo-checkout section)
 *
 * Internal steps:
 *   1 = Primary bike selection
 *   1.5 = Customize (hidden from progress bar)
 *   2 = Free bike selection
 *   3 = Checkout
 */

(function() {
  'use strict';

  // ===== STATE =====
  var currentStep = 1; // 1, 1.5, 2, or 3
  var selectedBikes = [null, null]; // [primary, free]
  var selectedUpgrades = [];
  var selectedProtection = [];

  // ===== INITIALIZATION =====
  document.addEventListener('DOMContentLoaded', function() {
    initUsBuilder();
  });

  function initUsBuilder() {
    setHeaderHeight();
    window.addEventListener('resize', debounce(setHeaderHeight, 100));

    initProductCardButtons();
    initProtectionCheckboxes();
    initUpgradeCheckboxes();

    updateUsProgressBar();
    updateUsStickyBar();
    bogoUsGoToStep(1);
  }

  // ===== HEADER HEIGHT =====
  function setHeaderHeight() {
    var totalHeight = 0;
    var headerSelectors = [
      '.shopify-section-header', 'header.section-header', '.header-section',
      '.site-header', '#shopify-section-header', 'header[role="banner"]', '.header-wrapper'
    ];

    var header = null;
    for (var i = 0; i < headerSelectors.length; i++) {
      header = document.querySelector(headerSelectors[i]);
      if (header) break;
    }

    if (header) {
      var styles = getComputedStyle(header);
      if (styles.position === 'sticky' || styles.position === 'fixed') {
        totalHeight = header.getBoundingClientRect().height;
      }
    }

    if (totalHeight > 0) {
      document.documentElement.style.setProperty('--header-height', totalHeight + 'px');
    } else {
      document.documentElement.style.removeProperty('--header-height');
    }
  }

  function debounce(func, wait) {
    var timeout;
    return function() {
      clearTimeout(timeout);
      timeout = setTimeout(func, wait);
    };
  }

  // ===== PRODUCT CARD BUTTONS =====
  function initProductCardButtons() {
    // Handle free bike grid clicks (product-card based)
    document.addEventListener('click', function(e) {
      var addBtn = e.target.closest('[data-action="add-to-bundle"]');
      if (!addBtn) return;

      var productCard = addBtn.closest('product-card');
      var freeGrid = addBtn.closest('#us-free-grid');

      if (freeGrid && productCard) {
        e.preventDefault();
        e.stopPropagation();
        handleFreeBikeSelection(productCard);
      }
    });

    // Variant dropdown changes
    document.addEventListener('change', function(e) {
      if (e.target.matches('[data-action="variant-change"]')) {
        var select = e.target;
        var productCard = select.closest('product-card');
        var selectedOption = select.options[select.selectedIndex];
        if (productCard && selectedOption) {
          updateProductCardVariant(productCard, selectedOption);
        }
      }
    });

    // Color swatch changes
    document.addEventListener('change', function(e) {
      if (e.target.matches('.color-swatch__radio')) {
        var radio = e.target;
        var productCard = radio.closest('product-card');
        if (productCard) {
          updateProductCardFromSwatch(productCard, radio);
        }
      }
    });
  }

  function updateProductCardVariant(productCard, selectedOption) {
    var variantId = selectedOption.dataset.id;
    var price = selectedOption.dataset.price;
    var mediaUrl = selectedOption.dataset.mediaUrl;
    var backdrop = selectedOption.dataset.backdrop;

    productCard.dataset.variantId = variantId;
    productCard.dataset.productPrice = price;

    var img = productCard.querySelector('.product-item__variant-image');
    if (img && mediaUrl) img.src = mediaUrl;

    var mediaDiv = productCard.querySelector('.product-item__media');
    if (mediaDiv && backdrop) {
      mediaDiv.className = mediaDiv.className.replace(/\b(white|gray|black|blue|red|green)\b/g, '');
      mediaDiv.classList.add(backdrop);
    }

    var priceEl = productCard.querySelector('[data-price]');
    if (priceEl && price) {
      var formattedPrice = (parseInt(price) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' }).replace('.00', '');
      priceEl.innerHTML = '<span class="visually-hidden">Sale price</span>' + formattedPrice;
    }

    var btn = productCard.querySelector('[data-action="add-to-bundle"]');
    if (btn) btn.dataset.id = variantId;

    // If this is the selected primary bike, update the customize summary
    if (selectedBikes[0] && productCard.dataset.productId === selectedBikes[0].id) {
      updatePrimaryBikeData(productCard);
    }
  }

  function updateProductCardFromSwatch(productCard, radio) {
    var variantId = radio.dataset.variantId;
    var price = radio.dataset.price;
    var mediaUrl = radio.dataset.mediaUrl;
    var backdrop = radio.dataset.backdrop;

    if (variantId) productCard.dataset.variantId = variantId;
    if (price) productCard.dataset.productPrice = price;

    var img = productCard.querySelector('.product-item__variant-image');
    if (img && mediaUrl) img.src = mediaUrl;

    var mediaDiv = productCard.querySelector('.product-item__media');
    if (mediaDiv && backdrop) {
      mediaDiv.className = mediaDiv.className.replace(/\b(white|gray|black|blue|red|green)\b/g, '');
      mediaDiv.classList.add(backdrop);
    }

    var priceEl = productCard.querySelector('[data-price]');
    if (priceEl && price) {
      var formattedPrice = (parseInt(price) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' }).replace('.00', '');
      priceEl.innerHTML = '<span class="visually-hidden">Sale price</span>' + formattedPrice;
    }

    var btn = productCard.querySelector('[data-action="add-to-bundle"]');
    if (btn && variantId) btn.dataset.id = variantId;

    var select = productCard.querySelector('[data-action="variant-change"]');
    if (select) {
      for (var i = 0; i < select.options.length; i++) {
        if (select.options[i].dataset.id === variantId) {
          select.selectedIndex = i;
          break;
        }
      }
    }

    // If this is the selected primary bike, update data
    if (selectedBikes[0] && productCard.dataset.productId === selectedBikes[0].id) {
      updatePrimaryBikeData(productCard);
    }
  }

  // ===== EXTRACT BIKE DATA FROM CARD =====
  function extractBikeData(cardElement) {
    var btn = cardElement.querySelector('[data-action="add-to-bundle"]');
    var variantSelect = cardElement.querySelector('[data-action="variant-change"]');
    var variantId;

    if (variantSelect && variantSelect.selectedIndex >= 0) {
      variantId = variantSelect.options[variantSelect.selectedIndex].dataset.id;
    }
    if (!variantId) {
      variantId = btn ? btn.dataset.id : (cardElement.dataset.variantId || cardElement.dataset.productId);
    }

    var currentImg = cardElement.querySelector('.product-item__variant-image');
    var imgSrc = currentImg ? currentImg.src : cardElement.dataset.productImage;

    var variantName = '';
    if (variantSelect && variantSelect.selectedIndex >= 0) {
      variantName = variantSelect.options[variantSelect.selectedIndex].textContent.trim();
    } else {
      var checkedSwatch = cardElement.querySelector('.color-swatch__radio:checked');
      if (checkedSwatch) {
        var label = cardElement.querySelector('.color-swatch__label[for="' + checkedSwatch.id + '"]');
        if (label) variantName = label.getAttribute('title') || label.textContent.trim();
      }
    }

    var shippingEta = '';
    if (variantSelect && variantSelect.selectedIndex >= 0) {
      var selOpt = variantSelect.options[variantSelect.selectedIndex];
      if (selOpt && selOpt.dataset.availability) shippingEta = selOpt.dataset.availability;
    }
    if (!shippingEta) {
      var swatch = cardElement.querySelector('.color-swatch__radio:checked');
      if (swatch && swatch.dataset.availability) shippingEta = swatch.dataset.availability;
    }
    if (!shippingEta) {
      var shipEl = cardElement.querySelector('.shipping-info_shipping-time');
      if (shipEl) shippingEta = shipEl.textContent.trim();
    }

    return {
      id: cardElement.dataset.productId,
      variantId: variantId,
      name: cardElement.dataset.productTitle,
      price: (parseInt(cardElement.dataset.productPrice) || 0) / 100,
      img: imgSrc,
      handle: cardElement.dataset.productHandle,
      variantName: variantName,
      shippingEta: shippingEta
    };
  }

  // ===== PRIMARY BIKE SELECTION (showcase cards — not product-bound) =====
  window.bogoUsSelectPrimary = function(cardElement) {
    var bikeData = {
      key: cardElement.dataset.bikeKey,
      name: cardElement.dataset.bikeName,
      price: parseInt(cardElement.dataset.bikePrice) || 0,
      img: cardElement.dataset.bikeImg,
      productUrl: cardElement.dataset.bikeUrl || '',
      defaultWheel: cardElement.dataset.defaultWheel || '',
      // These will be set later if the user selects a variant on the product page
      id: null,
      variantId: null,
      variantName: '',
      handle: cardElement.dataset.bikeKey,
      shippingEta: ''
    };

    selectedBikes[0] = bikeData;

    // Visual feedback
    var grid = document.getElementById('us-primary-grid');
    if (grid) {
      grid.querySelectorAll('.bogo-us-showcase-card').forEach(function(card) {
        card.classList.remove('selected');
      });
      cardElement.classList.add('selected');
    }

    // Enable the Next button
    var nextBtn = document.getElementById('us-step1-next');
    if (nextBtn) nextBtn.disabled = false;

    updateUsStickyBar();
    updateStoryBlocks();
  };

  // ===== FREE BIKE SELECTION =====
  function handleFreeBikeSelection(cardElement) {
    var bikeData = extractBikeData(cardElement);
    selectedBikes[1] = bikeData;

    // Visual feedback
    var grid = document.getElementById('us-free-grid');
    if (grid) {
      grid.querySelectorAll('product-card').forEach(function(card) {
        card.classList.remove('selected');
        var btnText = card.querySelector('[data-action="add-to-bundle"] span:first-child');
        if (btnText) btnText.textContent = 'Select Free Bike';
      });
      cardElement.classList.add('selected');
      var selectedBtn = cardElement.querySelector('[data-action="add-to-bundle"] span:first-child');
      if (selectedBtn) selectedBtn.textContent = '✓ Selected';
    }

    // Show confirmation panel
    showBikeConfirmation();
    updateUsStickyBar();
    updateStoryBlocks();
  }

  // ===== CUSTOMIZE SUMMARY =====
  function updateCustomizeSummary() {
    if (!selectedBikes[0]) return;
    var bike = selectedBikes[0];

    var imgEl = document.getElementById('us-customize-bike-img');
    var nameEl = document.getElementById('us-customize-bike-name');
    var priceEl = document.getElementById('us-customize-bike-price');

    if (imgEl) { imgEl.src = bike.img; imgEl.alt = bike.name; }
    if (nameEl) nameEl.textContent = bike.name;
    if (priceEl) priceEl.textContent = 'From $' + bike.price.toLocaleString();

    // Move the configurator section into the customize step slot
    moveConfiguratorIntoSlot();

    // Filter wheel type cards to only show options for the selected bike
    if (typeof window.bogoConfigFilterForBike === 'function') {
      window.bogoConfigFilterForBike(bike.key, bike.defaultWheel);
    }
  }

  // ===== CONFIGURATOR INTEGRATION =====
  function moveConfiguratorIntoSlot() {
    var slot = document.getElementById('us-configurator-slot');
    var cfgSection = document.querySelector('.bogo-us-configurator-wrapper');
    if (slot && cfgSection && !slot.contains(cfgSection)) {
      slot.appendChild(cfgSection);
      cfgSection.style.display = 'block';
    }
  }

  // Callback from bogo-us-configurator.js when user makes selections
  window.bogoUsUpdateFromConfigurator = function(data) {
    if (!selectedBikes[0]) return;

    // Update the primary bike with configurator data
    if (data.variantId) selectedBikes[0].variantId = data.variantId;
    if (data.name) selectedBikes[0].name = data.name;
    if (data.price) selectedBikes[0].price = data.price;
    if (data.handle) selectedBikes[0].handle = data.handle;
    if (data.img) {
      selectedBikes[0].img = data.img;
      var summaryImg = document.getElementById('us-customize-bike-img');
      if (summaryImg) summaryImg.src = data.img;
    }

    // Store configurator extras on the bike object
    selectedBikes[0].configExtras = {
      frameSize: data.frameSize || '',
      colour: data.colour || '',
      suspensionPrice: data.suspensionPrice || 0,
      suspensionItems: data.suspensionItems || [],
      addons: data.addons || {},
      additionalItems: data.additionalItems || [],
      properties: data.properties || {}
    };

    // Update summary display
    var nameEl = document.getElementById('us-customize-bike-name');
    var priceEl = document.getElementById('us-customize-bike-price');
    if (nameEl) nameEl.textContent = selectedBikes[0].name;

    // Calculate total including additional items
    var additionalTotal = (data.additionalItems || []).reduce(function(sum, item) {
      return sum + (item.price || 0);
    }, 0);
    var totalPrice = selectedBikes[0].price + (additionalTotal / 100); // prices are in cents
    if (priceEl) priceEl.textContent = '$' + totalPrice.toLocaleString();

    updateUsStickyBar();
  };

  // ===== BIKE CONFIRMATION =====
  function showBikeConfirmation() {
    if (!selectedBikes[0] || !selectedBikes[1]) return;

    var paidBike, freeBike;
    if (selectedBikes[0].price >= selectedBikes[1].price) {
      paidBike = selectedBikes[0];
      freeBike = selectedBikes[1];
    } else {
      paidBike = selectedBikes[1];
      freeBike = selectedBikes[0];
    }

    var freeGrid = document.getElementById('us-free-grid');
    var confirmation = document.getElementById('us-bike-confirmation');

    if (freeGrid) freeGrid.style.display = 'none';
    if (confirmation) {
      confirmation.style.display = 'block';

      document.getElementById('us-confirm-bike-1-img').src = paidBike.img;
      document.getElementById('us-confirm-bike-1-name').textContent = paidBike.name;
      document.getElementById('us-confirm-bike-1-price').textContent = '$' + paidBike.price.toLocaleString();
      document.getElementById('us-confirm-badge-1').textContent = 'YOU PAY';
      document.getElementById('us-confirm-badge-1').className = 'bogo-confirm-badge paid';

      document.getElementById('us-confirm-bike-2-img').src = freeBike.img;
      document.getElementById('us-confirm-bike-2-name').textContent = freeBike.name;
      document.getElementById('us-confirm-bike-2-price').textContent = '$' + freeBike.price.toLocaleString();
      document.getElementById('us-confirm-badge-2').textContent = 'FREE! 🎁';
      document.getElementById('us-confirm-badge-2').className = 'bogo-confirm-badge free';

      document.getElementById('us-confirm-savings').textContent = '$' + freeBike.price.toLocaleString();
    }

    setTimeout(function() { scrollToSection('#us-bike-confirmation'); }, 150);
  }

  // ===== UPGRADE CHECKBOXES =====
  function initUpgradeCheckboxes() {
    document.querySelectorAll('.bogo-us-upgrade-check').forEach(function(checkbox) {
      checkbox.addEventListener('change', function() {
        var type = this.dataset.type;
        var price = parseInt(this.dataset.price) || 0;
        var variantId = this.dataset.id;
        var name = this.dataset.name || type;

        if (this.checked) {
          selectedUpgrades.push({ type: type, price: price, variantId: variantId, name: name });
        } else {
          selectedUpgrades = selectedUpgrades.filter(function(u) { return u.variantId !== variantId; });
        }
        updateUsStickyBar();
      });
    });
  }

  // ===== PROTECTION CHECKBOXES (reuses bogo-checkout section) =====
  function initProtectionCheckboxes() {
    document.querySelectorAll('.bogo-protection-check').forEach(function(checkbox) {
      if (checkbox.disabled) return;
      checkbox.addEventListener('change', function() {
        var type = this.dataset.type;
        var bike = this.dataset.bike;
        var price = parseInt(this.dataset.price) || 0;
        var variantId = this.dataset.id;
        var name = this.dataset.name || type;
        var key = type + '-' + bike;

        if (this.checked) {
          selectedProtection.push({ key: key, type: type, bike: bike, price: price, variantId: variantId, name: name });
        } else {
          selectedProtection = selectedProtection.filter(function(p) { return p.key !== key; });
        }
        updateUsStickyBar();
        updateUsCheckoutSummary();
      });
    });
  }

  // ===== NAVIGATION =====
  window.bogoUsNavigateToStep = function(visibleStep) {
    // Map visible steps to internal steps
    if (visibleStep === 1) {
      if (currentStep >= 1) bogoUsGoToStep(1);
    } else if (visibleStep === 2) {
      if (selectedBikes[0]) bogoUsGoToStep(2);
    } else if (visibleStep === 3) {
      if (selectedBikes[0] && selectedBikes[1]) bogoUsGoToStep(3);
    }
  };

  window.bogoUsGoToStep = function(step) {
    currentStep = step;

    // Hide all bike selection steps
    document.querySelectorAll('.bogo-us-step').forEach(function(s) { s.classList.remove('active'); });

    // Hide checkout section wrapper
    var checkoutWrapper = document.querySelector('.bogo-checkout-wrapper');
    if (checkoutWrapper) checkoutWrapper.classList.remove('bogo-us-visible');

    // Show appropriate step
    if (step === 1) {
      var s1 = document.getElementById('bogo-us-step-1');
      if (s1) s1.classList.add('active');
    } else if (step === 1.5) {
      var sc = document.getElementById('bogo-us-customize');
      if (sc) sc.classList.add('active');
      updateCustomizeSummary();
      filterUpgradesForPrimaryBike();
    } else if (step === 2) {
      var s2 = document.getElementById('bogo-us-step-2');
      if (s2) s2.classList.add('active');
      updatePrimaryBanner();

      // Reset confirmation and show grid
      var confirmation = document.getElementById('us-bike-confirmation');
      var freeGrid = document.getElementById('us-free-grid');
      if (confirmation) confirmation.style.display = 'none';
      if (freeGrid) freeGrid.style.display = '';
    } else if (step === 3) {
      // Show the checkout section (hidden by default via CSS)
      if (checkoutWrapper) checkoutWrapper.classList.add('bogo-us-visible');
      updateUsCheckoutBikeCards();
      updateUsCheckoutSummary();
    }

    // Show/hide progress bar on checkout
    var progressBar = document.getElementById('bogo-progress-bar');
    if (step === 3) {
      if (progressBar) progressBar.style.display = 'none';
    } else {
      if (progressBar) progressBar.style.display = '';
    }

    updateUsProgressBar();
    updateUsStickyBar();
    updateStoryBlocks();

    // Scroll
    var scrollTargets = {
      1: '#bogo-us-step-1',
      1.5: '#bogo-us-customize',
      2: '#bogo-us-step-2',
      3: '.bogo-checkout-wrapper'
    };

    if (scrollTargets[step]) {
      setTimeout(function() { scrollToSection(scrollTargets[step]); }, 150);
    }
  };

  window.bogoUsGoToCustomize = function() {
    if (!selectedBikes[0]) return;
    bogoUsGoToStep(1.5);
  };

  window.bogoUsBackToPrimary = function() {
    bogoUsGoToStep(1);
  };

  window.bogoUsGoToFreeBike = function() {
    if (!selectedBikes[0]) return;
    bogoUsGoToStep(2);
  };

  window.bogoUsGoToCheckout = function() {
    if (!selectedBikes[0] || !selectedBikes[1]) return;
    bogoUsGoToStep(3);
  };

  window.bogoUsChangePrimary = function() {
    selectedBikes[0] = null;
    selectedUpgrades = [];
    lastLoadedUrl = '';

    // Clear visual selection on showcase cards
    var grid = document.getElementById('us-primary-grid');
    if (grid) {
      grid.querySelectorAll('.bogo-us-showcase-card').forEach(function(card) {
        card.classList.remove('selected');
      });
    }

    // Clear product embed
    var embed = document.getElementById('us-product-embed');
    if (embed) embed.innerHTML = '';

    // Uncheck all upgrades
    document.querySelectorAll('.bogo-us-upgrade-check').forEach(function(cb) { cb.checked = false; });

    // Disable step1 next button
    var nextBtn = document.getElementById('us-step1-next');
    if (nextBtn) nextBtn.disabled = true;

    bogoUsGoToStep(1);
  };

  window.bogoUsChangeFreeBike = function() {
    selectedBikes[1] = null;

    // Clear visual selection
    var grid = document.getElementById('us-free-grid');
    if (grid) {
      grid.querySelectorAll('product-card').forEach(function(card) {
        card.classList.remove('selected');
        var btn = card.querySelector('[data-action="add-to-bundle"] span:first-child');
        if (btn) btn.textContent = 'Select Free Bike';
      });
      grid.style.display = '';
    }

    var confirmation = document.getElementById('us-bike-confirmation');
    if (confirmation) confirmation.style.display = 'none';

    updateUsStickyBar();
  };

  // ===== FILTER UPGRADES =====
  function filterUpgradesForPrimaryBike() {
    var handle = selectedBikes[0] ? (selectedBikes[0].handle || '').toLowerCase().trim() : '';

    document.querySelectorAll('.bogo-us-upgrade-option').forEach(function(option) {
      var allowed = (option.dataset.allowedBikes || '').trim();
      if (!allowed) { option.style.display = ''; return; }

      var handles = allowed.split(',').map(function(h) { return h.trim().toLowerCase(); });
      var matched = handles.indexOf(handle) > -1;
      if (!matched) {
        for (var i = 0; i < handles.length; i++) {
          if (handles[i] && (handle.indexOf(handles[i]) > -1 || handles[i].indexOf(handle) > -1)) {
            matched = true;
            break;
          }
        }
      }

      if (matched) {
        option.style.display = '';
      } else {
        option.style.display = 'none';
        var cb = option.querySelector('.bogo-us-upgrade-check');
        if (cb && cb.checked) {
          cb.checked = false;
          cb.dispatchEvent(new Event('change'));
        }
      }
    });
  }

  // ===== PRIMARY BANNER =====
  function updatePrimaryBanner() {
    if (!selectedBikes[0]) return;
    var bike = selectedBikes[0];

    var imgEl = document.getElementById('us-banner-bike-img');
    var nameEl = document.getElementById('us-banner-bike-name');
    var priceEl = document.getElementById('us-banner-bike-price');

    if (imgEl) { imgEl.src = bike.img; imgEl.alt = bike.name; }
    if (nameEl) nameEl.textContent = bike.name;
    if (priceEl) priceEl.textContent = '$' + bike.price.toLocaleString();
  }

  // ===== PROGRESS BAR =====
  function updateUsProgressBar() {
    // Map internal step to visible step: 1/1.5 → 1, 2 → 2, 3 → 3
    var visibleStep;
    if (currentStep <= 1.5) visibleStep = 1;
    else if (currentStep === 2) visibleStep = 2;
    else visibleStep = 3;

    var steps = document.querySelectorAll('.bogo-progress-step');
    steps.forEach(function(step) {
      var s = parseInt(step.dataset.step);
      var circle = step.querySelector('.bogo-step-circle');
      var label = step.querySelector('.bogo-step-label');

      circle.classList.remove('active', 'completed');
      label.classList.remove('active');
      step.classList.remove('clickable');

      if (s < visibleStep) {
        circle.classList.add('completed');
        circle.innerHTML = '';
        step.classList.add('clickable');
      } else if (s === visibleStep) {
        circle.classList.add('active');
        circle.textContent = s;
        label.classList.add('active');
        step.classList.add('clickable');
      } else {
        circle.textContent = s;
      }
    });

    // Progress lines
    var line1 = document.getElementById('us-progress-line-1');
    var line2 = document.getElementById('us-progress-line-2');
    if (line1) line1.style.width = visibleStep > 1 ? '100%' : (currentStep === 1.5 ? '50%' : '0%');
    if (line2) line2.style.width = visibleStep > 2 ? '100%' : '0%';
  }

  // ===== STICKY BAR =====
  function updateUsStickyBar() {
    // Show/hide the sticky bar
    var bar = document.getElementById('bogo-checkout-bar');
    if (bar) {
      if (selectedBikes[0] || selectedBikes[1]) {
        bar.classList.add('visible');
      } else {
        bar.classList.remove('visible');
      }
    }

    var slot1 = document.getElementById('bar-slot-1');
    var slot2 = document.getElementById('bar-slot-2');

    if (slot1) {
      if (selectedBikes[0]) {
        slot1.classList.add('filled');
        slot1.innerHTML = '<img src="' + selectedBikes[0].img + '" alt="">';
      } else {
        slot1.classList.remove('filled');
        slot1.innerHTML = '+';
      }
    }

    if (slot2) {
      if (selectedBikes[1]) {
        slot2.classList.add('filled');
        slot2.innerHTML = '<img src="' + selectedBikes[1].img + '" alt="">';
      } else {
        slot2.classList.remove('filled');
        slot2.innerHTML = '+';
      }
    }

    var bikeCount = selectedBikes.filter(function(b) { return b !== null; }).length;
    var countEl = document.getElementById('bar-count');
    if (countEl) countEl.textContent = bikeCount;

    // Calculate totals
    var total = 0;
    var original = 0;

    if (selectedBikes[0] && selectedBikes[1]) {
      var bike1 = selectedBikes[0];
      var bike2 = selectedBikes[1];
      var paidBike = bike1.price >= bike2.price ? bike1 : bike2;
      total = paidBike.price;
      original = bike1.price + bike2.price;
    } else if (selectedBikes[0]) {
      total = selectedBikes[0].price;
      original = selectedBikes[0].price;
    }

    // Add configurator extras (additional items from product page)
    var configExtras = (selectedBikes[0] && selectedBikes[0].configExtras) ? selectedBikes[0].configExtras : {};
    var cfgAdditional = configExtras.additionalItems || [];
    cfgAdditional.forEach(function(item) { total += (item.price || 0) / 100; }); // prices in cents

    // Add legacy upgrades
    var upgradesTotal = selectedUpgrades.reduce(function(sum, u) { return sum + u.price; }, 0);
    total += upgradesTotal;

    // Add protection
    var protectionTotal = selectedProtection.reduce(function(sum, p) { return sum + p.price; }, 0);
    total += protectionTotal;

    var totalEl = document.getElementById('bar-total');
    var originalEl = document.getElementById('bar-original');
    if (totalEl) totalEl.textContent = '$' + total.toLocaleString();
    if (originalEl) originalEl.textContent = original > 0 && original !== total ? '$' + original.toLocaleString() : '';

    // Button state
    var btn = document.getElementById('bar-btn');
    if (btn) {
      btn.classList.remove('secondary', 'checkout');

      if (currentStep === 1) {
        btn.onclick = function() { if (selectedBikes[0]) bogoUsGoToCustomize(); };
        btn.disabled = !selectedBikes[0];
        btn.textContent = selectedBikes[0] ? 'Customize Your Bike →' : 'Select Your eBike';
      } else if (currentStep === 1.5) {
        btn.onclick = function() { bogoUsGoToFreeBike(); };
        btn.disabled = false;
        btn.textContent = 'Choose Free Bike →';
      } else if (currentStep === 2) {
        btn.onclick = function() { if (selectedBikes[1]) bogoUsGoToCheckout(); };
        btn.disabled = !selectedBikes[1];
        btn.textContent = selectedBikes[1] ? 'Continue to Checkout →' : 'Select Free Bike';
      } else if (currentStep === 3) {
        btn.onclick = function() { bogoUsCompletePurchase(); };
        btn.disabled = false;
        btn.textContent = 'Complete Purchase →';
        btn.classList.add('checkout');
      }
    }
  }

  // ===== CHECKOUT (reuses bogo-checkout section) =====
  function filterCheckoutUpgradesForBike(bikeNum, bikeHandle) {
    var section = document.querySelector('.bogo-checkout-bike-upgrades[data-bike="' + bikeNum + '"]');
    if (!section) return;
    var handle = (bikeHandle || '').toLowerCase().trim();

    if (!handle) {
      section.querySelectorAll('.bogo-upgrade-option').forEach(function(o) { o.style.display = ''; });
      return;
    }

    section.querySelectorAll('.bogo-upgrade-option').forEach(function(option) {
      var allowed = (option.dataset.allowedBikes || '').trim();
      if (!allowed) { option.style.display = ''; return; }

      var handles = allowed.split(',').map(function(h) { return h.trim().toLowerCase(); });
      var matched = handles.indexOf(handle) > -1;
      if (!matched) {
        for (var i = 0; i < handles.length; i++) {
          if (handles[i] && (handle.indexOf(handles[i]) > -1 || handles[i].indexOf(handle) > -1)) {
            matched = true;
            break;
          }
        }
      }

      if (matched) {
        option.style.display = '';
      } else {
        option.style.display = 'none';
        var cb = option.querySelector('.bogo-protection-check');
        if (cb && cb.checked) { cb.checked = false; cb.dispatchEvent(new Event('change')); }
      }
    });
  }

  function updateUsCheckoutBikeCards() {
    if (!selectedBikes[0] || !selectedBikes[1]) return;

    var paidBike, freeBike;
    if (selectedBikes[0].price >= selectedBikes[1].price) {
      paidBike = selectedBikes[0];
      freeBike = selectedBikes[1];
    } else {
      paidBike = selectedBikes[1];
      freeBike = selectedBikes[0];
    }

    var b1Name = document.getElementById('checkout-bike1-name');
    var b1Price = document.getElementById('checkout-bike1-price');
    var b1Img = document.getElementById('checkout-bike1-img');
    var b1Variant = document.getElementById('checkout-bike1-variant');
    var b1Shipping = document.getElementById('checkout-bike1-shipping');

    if (b1Name) b1Name.textContent = paidBike.name;
    if (b1Price) b1Price.textContent = '$' + paidBike.price.toLocaleString();
    if (b1Img) { b1Img.src = paidBike.img; b1Img.alt = paidBike.name; }
    if (b1Variant) b1Variant.textContent = paidBike.variantName || '';
    if (b1Shipping && paidBike.shippingEta) b1Shipping.innerHTML = '<i class="ph ph-truck"></i> ' + paidBike.shippingEta;

    var b2Name = document.getElementById('checkout-bike2-name');
    var b2Original = document.getElementById('checkout-bike2-original');
    var b2Img = document.getElementById('checkout-bike2-img');
    var b2Variant = document.getElementById('checkout-bike2-variant');
    var b2Shipping = document.getElementById('checkout-bike2-shipping');

    if (b2Name) b2Name.textContent = freeBike.name;
    if (b2Original) b2Original.textContent = '$' + freeBike.price.toLocaleString();
    if (b2Img) { b2Img.src = freeBike.img; b2Img.alt = freeBike.name; }
    if (b2Variant) b2Variant.textContent = freeBike.variantName || '';
    if (b2Shipping && freeBike.shippingEta) b2Shipping.innerHTML = '<i class="ph ph-truck"></i> ' + freeBike.shippingEta;

    filterCheckoutUpgradesForBike('1', paidBike.handle);
    filterCheckoutUpgradesForBike('2', freeBike.handle);
  }

  function updateUsCheckoutSummary() {
    if (!selectedBikes[0] || !selectedBikes[1]) return;

    var paidBike, freeBike;
    if (selectedBikes[0].price >= selectedBikes[1].price) {
      paidBike = selectedBikes[0];
      freeBike = selectedBikes[1];
    } else {
      paidBike = selectedBikes[1];
      freeBike = selectedBikes[0];
    }

    var original = selectedBikes[0].price + selectedBikes[1].price;
    var savings = freeBike.price;
    var total = paidBike.price;

    var upgradesTotal = selectedUpgrades.reduce(function(sum, u) { return sum + u.price; }, 0);
    total += upgradesTotal;

    var protectionTotal = selectedProtection.reduce(function(sum, p) { return sum + p.price; }, 0);
    total += protectionTotal;

    var subtotalEl = document.getElementById('checkout-subtotal');
    var savingsEl = document.getElementById('checkout-savings');
    var totalEl = document.getElementById('checkout-total');

    if (subtotalEl) subtotalEl.textContent = '$' + original.toLocaleString();
    if (savingsEl) savingsEl.textContent = '-$' + savings.toLocaleString();

    // Upgrades row
    var upgradesRow = document.getElementById('protection-row');
    var upgradesEl = document.getElementById('checkout-protection');
    var combinedTotal = upgradesTotal + protectionTotal;
    if (upgradesRow && upgradesEl) {
      if (combinedTotal > 0) {
        upgradesRow.style.display = 'flex';
        upgradesEl.textContent = '+$' + combinedTotal.toLocaleString();
      } else {
        upgradesRow.style.display = 'none';
      }
    }

    // Populate protection list
    var protectionSection = document.getElementById('checkout-protection-section');
    var protectionList = document.getElementById('checkout-protection-list');
    var allItems = selectedUpgrades.concat(selectedProtection);
    if (protectionSection && protectionList) {
      if (allItems.length > 0) {
        protectionSection.style.display = 'block';
        var html = '';
        allItems.forEach(function(item) {
          html += '<div class="bogo-checkout-accessory-item"><span>' + item.name + '</span><span>$' + item.price.toLocaleString() + '</span></div>';
        });
        protectionList.innerHTML = html;
      } else {
        protectionSection.style.display = 'none';
      }
    }

    if (totalEl) totalEl.textContent = '$' + total.toLocaleString() + ' USD';
  }

  // ===== COMPLETE PURCHASE =====
  window.bogoUsCompletePurchase = function() {
    var paidBike, freeBike;
    if (selectedBikes[0] && selectedBikes[1]) {
      if (selectedBikes[0].price >= selectedBikes[1].price) {
        paidBike = selectedBikes[0];
        freeBike = selectedBikes[1];
      } else {
        paidBike = selectedBikes[1];
        freeBike = selectedBikes[0];
      }
    }

    var cartItems = [];

    // Add bikes
    selectedBikes.forEach(function(bike, index) {
      if (bike) {
        var isFree = freeBike && bike.variantId === freeBike.variantId;
        var bikeKey = 1000000000 + Math.floor(Math.random() * 9000000000);
        cartItems.push({
          id: bike.variantId || bike.id,
          quantity: 1,
          properties: Object.assign({
            '_bogo': 'true',
            '_bike_number': (index + 1).toString(),
            '_bogo_free': isFree ? 'true' : 'false',
            '_key': bikeKey.toString()
          }, bike.shippingEta ? { 'Estimated Delivery': bike.shippingEta } : {})
        });
      }
    });

    // Add configurator extras (suspension tier products, customisations)
    var configExtras = (selectedBikes[0] && selectedBikes[0].configExtras) ? selectedBikes[0].configExtras : {};

    // Suspension tier products
    if (configExtras.suspensionItems && configExtras.suspensionItems.length > 0) {
      configExtras.suspensionItems.forEach(function(variantId) {
        if (variantId) {
          cartItems.push({
            id: variantId,
            quantity: 1,
            properties: { '_bogo_suspension': 'true', '_for_bike': '1' }
          });
        }
      });
    }

    // Configurator add-ons (battery, dropper, headlights, etc.)
    var cfgAddons = configExtras.addons || {};
    Object.keys(cfgAddons).forEach(function(key) {
      var addon = cfgAddons[key];
      if (addon && addon.variantId) {
        cartItems.push({
          id: addon.variantId,
          quantity: 1,
          properties: { '_bogo_addon': 'true', '_addon_type': key, '_for_bike': '1' }
        });
      }
    });

    // Add additional items from the product page (Fork, Brakes, Battery, etc.)
    var additionalItems = configExtras.additionalItems || [];
    additionalItems.forEach(function(item) {
      if (item.variantId) {
        cartItems.push({
          id: item.variantId,
          quantity: 1,
          properties: { '_bogo_additional': 'true', '_for_bike': '1' }
        });
      }
    });

    // Add product properties (Fork selection name, Brakes selection name, etc.)
    // These get attached to the primary bike's cart item
    var additionalProperties = configExtras.properties || {};
    if (Object.keys(additionalProperties).length > 0 && cartItems.length > 0) {
      // Merge properties into the first bike's cart item
      Object.keys(additionalProperties).forEach(function(key) {
        cartItems[0].properties[key] = additionalProperties[key];
      });
    }

    // Add legacy upgrades (from old upgrade checkboxes if any exist)
    selectedUpgrades.forEach(function(upgrade) {
      if (upgrade.variantId) {
        cartItems.push({
          id: upgrade.variantId,
          quantity: 1,
          properties: { '_bogo_upgrade': 'true', '_upgrade_type': upgrade.type }
        });
      }
    });

    // Add protection plans (from checkout section)
    selectedProtection.forEach(function(protection) {
      if (protection.variantId) {
        cartItems.push({
          id: protection.variantId,
          quantity: 1,
          properties: { '_bogo_protection': 'true', '_protection_type': protection.type, '_for_bike': protection.bike }
        });
      }
    });

    console.log('BOGO US Cart Items:', cartItems);

    var ctaBtn = document.querySelector('.bogo-checkout-cta-btn');
    if (ctaBtn) {
      ctaBtn.disabled = true;
      ctaBtn.setAttribute('data-original-text', ctaBtn.textContent);
      ctaBtn.innerHTML = '<span class="bogo-spinner"></span> Processing...';
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: cartItems })
    })
    .then(function(response) {
      if (!response.ok) throw new Error('Failed to add items to cart');
      return response.json();
    })
    .then(function(data) {
      console.log('Added to cart:', data);
      if (ctaBtn) ctaBtn.innerHTML = '<span class="bogo-spinner"></span> Redirecting...';
      window.location.href = '/checkout';
    })
    .catch(function(error) {
      console.error('Cart error:', error);
      if (ctaBtn) {
        ctaBtn.disabled = false;
        ctaBtn.textContent = ctaBtn.getAttribute('data-original-text') || 'Complete Purchase →';
      }
      alert('There was an error adding items to cart. Please try again.');
    });
  };

  // Alias for bogo-checkout section's onclick
  window.bogoCompletePurchase = window.bogoUsCompletePurchase;

  // ===== INFO MODAL (reused from bogo-checkout) =====
  window.bogoOpenInfoModal = function(btn) {
    var modal = document.getElementById('bogo-info-modal');
    var titleEl = document.getElementById('bogo-info-modal-title');
    var bodyEl = document.getElementById('bogo-info-modal-body');
    if (!modal || !titleEl || !bodyEl) return;

    titleEl.textContent = btn.dataset.infoTitle || '';

    var pageHandle = (btn.dataset.infoPage || '').trim();
    if (pageHandle) {
      bodyEl.innerHTML = '<p style="text-align:center;padding:20px;">Loading...</p>';
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.setAttribute('modal-overlay-open', 'true');

      fetch('/pages/' + pageHandle + '.json')
        .then(function(r) { return r.json(); })
        .then(function(data) { bodyEl.innerHTML = data.page.body_html; })
        .catch(function() { bodyEl.innerHTML = btn.dataset.infoContent || '<p>Could not load info.</p>'; });
    } else {
      bodyEl.innerHTML = btn.dataset.infoContent || '';
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.setAttribute('modal-overlay-open', 'true');
    }
  };

  window.bogoCloseInfoModal = function() {
    var modal = document.getElementById('bogo-info-modal');
    if (modal) modal.setAttribute('aria-hidden', 'true');
    document.documentElement.setAttribute('modal-overlay-open', 'false');
  };

  document.addEventListener('click', function(e) {
    var modal = document.getElementById('bogo-info-modal');
    if (!modal || modal.getAttribute('aria-hidden') === 'true') return;
    if (e.target === modal) bogoCloseInfoModal();
  });

  // ===== BIKE STORYTELLING BLOCKS =====
  function updateStoryBlocks() {
    var container = document.getElementById('bogo-storytelling');
    if (!container) return;

    var blocks = container.querySelectorAll('.bogo-story-block');
    var stepStr = String(currentStep);
    var bikeKey = '';

    // Steps 1 and 2: show ALL story blocks for that step (no bike filter)
    // Steps 1.5 and 3: filter by selected bike(s)
    if (currentStep === 1 || currentStep === 2) {
      blocks.forEach(function(block) {
        block.style.display = (block.dataset.forStep === stepStr) ? '' : 'none';
      });
      return;
    }

    if (currentStep === 1.5) {
      bikeKey = selectedBikes[0] ? (selectedBikes[0].key || selectedBikes[0].handle || '') : '';
    } else if (currentStep === 3) {
      // On checkout, show blocks for both bikes
      var key1 = selectedBikes[0] ? (selectedBikes[0].key || selectedBikes[0].handle || '') : '';
      var key2 = selectedBikes[1] ? (selectedBikes[1].handle || selectedBikes[1].key || '') : '';
      blocks.forEach(function(block) {
        var forStep = block.dataset.forStep;
        var forBike = (block.dataset.forBike || '').trim();
        if (forStep !== '3') {
          block.style.display = 'none';
          return;
        }
        if (!forBike || forBike === key1 || forBike === key2) {
          block.style.display = '';
        } else {
          block.style.display = 'none';
        }
      });
      return;
    }

    blocks.forEach(function(block) {
      var forStep = block.dataset.forStep;
      var forBike = (block.dataset.forBike || '').trim();

      if (forStep !== stepStr) {
        block.style.display = 'none';
        return;
      }

      if (!forBike) {
        block.style.display = bikeKey ? '' : 'none';
        return;
      }

      if (bikeKey && forBike === bikeKey) {
        block.style.display = '';
      } else {
        block.style.display = 'none';
      }
    });
  }

  // ===== SCROLL HELPER =====
  function scrollToSection(selector) {
    var element = document.querySelector(selector);
    if (!element) return;
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ===== QUICK VIEW (delegates to existing bogo-quick-view) =====
  window.bogoOpenQuickView = function(productId, blockId) {
    var modal = document.getElementById('bogo-qv-' + productId + '-' + blockId);
    if (!modal) return;
    modal.style.display = '';
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('no-mobile-scroll');
  };

  function bogoCloseQuickView(backdrop) {
    if (!backdrop) return;
    backdrop.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('no-mobile-scroll');
    setTimeout(function() { backdrop.style.display = 'none'; }, 300);
  }

  // Close Quick View: X button, Back to Bikes button, backdrop click, Escape key
  document.addEventListener('click', function(e) {
    // X button or Back to Bikes button
    if (e.target.closest('[data-action="close-quick-view"]')) {
      var backdrop = e.target.closest('.bogo-qv-backdrop');
      if (backdrop) bogoCloseQuickView(backdrop);
      return;
    }

    // Backdrop click (clicking the dark overlay, not the modal)
    if (e.target.classList.contains('bogo-qv-backdrop') && e.target.getAttribute('aria-hidden') === 'false') {
      bogoCloseQuickView(e.target);
    }
  });

  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') {
      var openModal = document.querySelector('.bogo-qv-backdrop[aria-hidden="false"]');
      if (openModal) bogoCloseQuickView(openModal);
    }
  });

})();
